import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { Link, useNavigate, useParams } from 'react-router-dom';

import { Alert, notification } from 'antd';
import type { BreadcrumbItemType } from 'antd/es/breadcrumb/Breadcrumb';

import { ASSET_TYPES, getAssetTypeName } from '@/shared/api/openmetadata';
import {
  getAssetIconStyle,
  getAssetIconWrapperStyle,
  getAssetTypeIcon,
  getDefaultTabByAssetType,
} from '@/shared/lib/asset';
import { parseFqnWithTypes, removeFqnQuotes } from '@/shared/lib/format';
import type { FqnPart } from '@/shared/lib/format';
import { buildAssetDetailUrl } from '@/shared/lib/url';
import { Button } from '@/shared/ui/Button';
import { ContentHeader } from '@/shared/ui/ContentHeader';
import { DescriptionEditModal } from '@/shared/ui/DescriptionEditModal';
import { EllipsisText } from '@/shared/ui/EllipsisText';
import { ErrorBoundary } from '@/shared/ui/ErrorBoundary';
import { ServiceIcon } from '@/shared/ui/ServiceIcon';
import { LoadingState } from '@/shared/ui/states/LoadingState';
import { StatusBadge } from '@/shared/ui/StatusBadge';

import { convertToTagItems, filterEntityLabels } from '@/entities/search';

import { useAssetBaseInfo, useUpdateAssetDescription } from '@/features/asset-detail';
import { FavoriteButton } from '@/features/favorites';

import { AssetDetailContent, AssetDetailSidebar, AssetDetailSummaryCard } from '@/widgets/asset-detail';

import styles from './AssetDetailPage.module.css';

const ASSET_TYPE_ICON_SIZE = 24;
const SERVICE_ICON_SIZE = 24;

const AssetDetailPageContent: React.FC = () => {
  const params = useParams<{
    entityType?: string;
    fqn?: string;
    serviceType?: string;
    serviceName?: string;
    tab?: string;
  }>();

  const originalEntityType = params.serviceType ? ASSET_TYPES.SERVICE : params.entityType || '';
  let entityType = originalEntityType;
  const fqn =
    params.serviceType && params.serviceName ? `${params.serviceType}/${params.serviceName}` : params.fqn || '';

  if (originalEntityType === 'feature_set') {
    entityType = ASSET_TYPES.TABLE;
  } else if (originalEntityType === 'dataset') {
    entityType = ASSET_TYPES.TABLE;
  }

  const tab = params.tab;

  const navigate = useNavigate();

  const [currentTab, setCurrentTab] = useState<string>('');
  const [isLineageFullscreen, setIsLineageFullscreen] = useState(false);
  const [isDescriptionModalOpen, setIsDescriptionModalOpen] = useState(false);

  useEffect(() => {
    if (tab) {
      setCurrentTab(tab);
    } else {
      let defaultTab = '';

      if (entityType === ASSET_TYPES.SERVICE) {
        defaultTab = 'insights';
      } else {
        defaultTab = getDefaultTabByAssetType(entityType);
      }

      if (!defaultTab) {
        notification.error({
          message: '지원하지 않는 자산 타입',
          description: `"${entityType}" 타입의 자산은 현재 지원하지 않습니다.`,
          duration: 3,
          placement: 'bottomLeft',
        });
        navigate(-1);
        return;
      }

      setCurrentTab(defaultTab);
      if (defaultTab) {
        if (entityType === ASSET_TYPES.SERVICE) {
          const [serviceType, serviceName] = fqn.split('/');
          navigate(`/service/${serviceType}/${serviceName}/${defaultTab}`, { replace: true });
        } else {
          navigate(`/${originalEntityType}/${fqn}/${defaultTab}`, { replace: true });
        }
      }
    }
  }, [tab, entityType, originalEntityType, fqn, navigate]);

  const shouldFetchBaseInfo = !!entityType && !!fqn && !!currentTab;
  const { data: assetData, isLoading, error, refetch } = useAssetBaseInfo(entityType, fqn, shouldFetchBaseInfo);
  const updateDescriptionMutation = useUpdateAssetDescription();
  const serviceType = assetData?.serviceType ?? params.serviceType;

  const iconConfig = useMemo(() => {
    const displayEntityType =
      originalEntityType === 'feature_set' || originalEntityType === 'dataset' ? originalEntityType : entityType;
    const { iconColor } = getAssetIconStyle(displayEntityType);

    const component =
      entityType === ASSET_TYPES.SERVICE && serviceType ? (
        <ServiceIcon serviceType={serviceType} size={SERVICE_ICON_SIZE} />
      ) : (
        getAssetTypeIcon(displayEntityType, styles.icon, ASSET_TYPE_ICON_SIZE, iconColor)
      );

    return {
      component,
      wrapperStyle: getAssetIconWrapperStyle(displayEntityType),
      tooltip: getAssetTypeName(displayEntityType),
    };
  }, [entityType, originalEntityType, serviceType]);

  const fqnParts = useMemo<FqnPart[]>(() => {
    return parseFqnWithTypes(fqn, entityType);
  }, [fqn, entityType]);

  const displayName = assetData?.displayName || assetData?.name || fqnParts[fqnParts.length - 1]?.value || '';

  const classificationTags = useMemo(() => {
    return convertToTagItems(
      filterEntityLabels(assetData?.tags, {
        includeOnlySource: 'Classification',
        excludeTier: true,
        excludeCertification: true,
      })
    );
  }, [assetData]);

  const classificationTagsDisplay = useMemo(() => {
    return convertToTagItems(
      filterEntityLabels(assetData?.tags, {
        includeOnlySource: 'Classification',
        excludeTier: true,
        excludeCertification: true,
        requireConfirmed: true,
      })
    );
  }, [assetData]);

  const glossaryTerms = useMemo(() => {
    return convertToTagItems(
      filterEntityLabels(assetData?.tags, {
        includeOnlySource: 'Glossary',
      })
    );
  }, [assetData]);

  const glossaryTermsDisplay = useMemo(() => {
    return convertToTagItems(
      filterEntityLabels(assetData?.tags, {
        includeOnlySource: 'Glossary',
        requireConfirmed: true,
      })
    );
  }, [assetData]);

  const breadcrumbItems = useMemo<BreadcrumbItemType[]>(() => {
    if (originalEntityType === 'feature_set' || originalEntityType === 'dataset') {
      return [
        {
          title: (
            <span className={styles.breadcrumbItem}>
              <EllipsisText text={displayName} className={styles.breadcrumbText} />
            </span>
          ),
        },
      ];
    }

    return fqnParts.map((part, index) => {
      const isLast = index === fqnParts.length - 1;

      if (isLast) {
        return {
          title: (
            <span className={styles.breadcrumbItem}>
              <EllipsisText text={displayName} className={styles.breadcrumbText} />
            </span>
          ),
        };
      }

      if (part.entityType === ASSET_TYPES.SERVICE && assetData?.service?.type) {
        const serviceType = assetData.service.type;
        return {
          title: (
            <Link to={`/service/${serviceType}/${part.value}`} className={styles.breadcrumbItem}>
              <EllipsisText text={part.value} className={styles.breadcrumbText} />
            </Link>
          ),
        };
      }

      return {
        title: (
          <Link to={`/${part.entityType}/${removeFqnQuotes(part.fqn)}`} className={styles.breadcrumbItem}>
            <EllipsisText text={part.value} className={styles.breadcrumbText} />
          </Link>
        ),
      };
    });
  }, [fqnParts, displayName, assetData?.service?.type, originalEntityType]);

  const handleGlossaryTermClick = useCallback(
    (termFqn: string) => {
      const detailUrl = buildAssetDetailUrl('glossaryTerm', termFqn);
      navigate(detailUrl);
    },
    [navigate]
  );

  const handleDescriptionEdit = useCallback(() => {
    setIsDescriptionModalOpen(true);
  }, []);

  const handleDescriptionModalClose = useCallback(() => {
    setIsDescriptionModalOpen(false);
  }, []);

  const handleDescriptionSubmit = useCallback(
    async (description: string) => {
      if (!assetData?.id) {
        return;
      }

      await updateDescriptionMutation.mutateAsync({
        entityType,
        entityId: assetData.id,
        fqn,
        newDescription: description,
      });
    },
    [assetData?.id, entityType, fqn, updateDescriptionMutation]
  );

  if (!currentTab || isLoading) {
    return (
      <div className={styles.assetDetailPage}>
        <LoadingState message="자산 정보를 불러오는 중..." />
      </div>
    );
  }

  if (error || !assetData || !assetData.id) {
    return (
      <div className={styles.assetDetailPage}>
        <div className={styles.errorState}>
          <Alert
            message="자산 정보를 불러오는데 실패했습니다"
            description={error?.message || '정보를 불러오지 못했습니다. 다시 시도해 주세요.'}
            type="error"
            showIcon
          />
          <div className={styles.errorActions}>
            <Button variant="filled" color="standard" onClick={() => refetch()}>
              다시 시도
            </Button>
            <Button variant="outlined" color="standard" onClick={() => navigate('/')}>
              홈으로
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.assetDetailPage}>
      {!isLineageFullscreen && (
        <>
          <div className={styles.headerSection}>
            <ContentHeader
              breadcrumbItems={breadcrumbItems}
              icon={iconConfig}
              title={displayName}
              titleRowExtra={<StatusBadge deleted={assetData?.deleted} entityStatus={assetData?.entityStatus} />}
              description={assetData?.description}
              descriptionEditable
              onDescriptionEdit={handleDescriptionEdit}
              actions={
                <FavoriteButton
                  assetId={assetData?.id || fqn}
                  assetName={assetData?.displayName || assetData?.name || fqn}
                  assetType={entityType}
                  assetFqn={fqn}
                />
              }
            />
            <DescriptionEditModal
              open={isDescriptionModalOpen}
              currentDescription={assetData?.description}
              isLoading={updateDescriptionMutation.isPending}
              onClose={handleDescriptionModalClose}
              onSubmit={handleDescriptionSubmit}
            />
            <AssetDetailSummaryCard
              assetData={assetData}
              entityType={entityType}
              serviceType={params.serviceType}
              fqn={fqn}
            />
          </div>
          <div className={styles.mainSection}>
            <div className={styles.mainContent}>
              <div className={styles.contentArea}>
                <AssetDetailContent
                  entityType={entityType}
                  fqn={fqn}
                  entityId={assetData.id}
                  currentTab={currentTab}
                  assetData={assetData}
                  isLineageFullscreen={isLineageFullscreen}
                  onToggleLineageFullscreen={() => setIsLineageFullscreen(!isLineageFullscreen)}
                />
              </div>
              <div className={styles.sidebarArea}>
                <AssetDetailSidebar
                  entityType={entityType}
                  entityId={assetData.id}
                  fqn={fqn}
                  classificationTags={classificationTags}
                  classificationTagsDisplay={classificationTagsDisplay}
                  glossaryTerms={glossaryTerms}
                  glossaryTermsDisplay={glossaryTermsDisplay}
                  onTermClick={handleGlossaryTermClick}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {isLineageFullscreen && (
        <div className={styles.fullscreenContent}>
          <AssetDetailContent
            entityType={entityType}
            fqn={fqn}
            entityId={assetData.id}
            currentTab={currentTab}
            assetData={assetData}
            isLineageFullscreen={isLineageFullscreen}
            onToggleLineageFullscreen={() => setIsLineageFullscreen(!isLineageFullscreen)}
          />
        </div>
      )}
    </div>
  );
};

export const AssetDetailPage: React.FC = () => {
  return (
    <ErrorBoundary
      title="자산 상세 정보를 불러올 수 없습니다"
      description="잘못된 경로이거나 자산이 존재하지 않습니다. 경로를 확인하거나 홈으로 돌아가세요.">
      <AssetDetailPageContent />
    </ErrorBoundary>
  );
};
